import { useRef } from 'react';
import { translate } from '../../../lib/lang.helper';
import { useSelector } from 'react-redux';
import { selectLang } from '../../../store/ui/ui.selector';
import { Dropdown, Input, CheckboxButton } from '../../shared';
import { SelectIsMyFarm } from '../../../store/extra/extra.selector';
import OysterLineProbTable, {
  OysterProbRef,
} from '../../farm-util/OysterLineProbTable';
import { SelectAssessFields } from '../../../store/extra/extra.selector';
import { ICustomValue } from '../../../entities/automation.entities';
import { Link } from 'react-router-dom';
import { IOysterLineResource } from '../../../entities/farms.entities';
import { getAverageSize, numToStr } from '../../../lib/common.helpers';

interface IFormData {
  estimated_amount?: number | null;
  estimated_dozens?: number | null;
  size_min?: number | null;
  size_max?: number | null;
  size_avg?: number | null;
  ready_to_harvest?: boolean;
  custom_values?: ICustomValue[];
}

interface Props {
  lineData: IOysterLineResource;
  formData: IFormData;
  onChange: (data: any) => void;
  measureUnit?: string;
  disabled?: boolean;
}

const InstantOysterAssessment = ({
  lineData,
  formData,
  onChange,
  measureUnit,
  disabled,
}: Props) => {
  const lang = useSelector(selectLang);
  const isMyFarm = SelectIsMyFarm(lineData.farm_id);
  const tableRef = useRef<OysterProbRef>(null);
  const customAssesFields = SelectAssessFields(lineData.farm_id);

  const updateCustomAssessValue = (fieldID: number, val: string) => {
    let tmp = [...(formData.custom_values ?? [])];
    const i = tmp.findIndex(x => x.field_id === fieldID);
    const field = customAssesFields.find(x => x.id === fieldID);
    if (!field) return;

    if (i >= 0) {
      tmp[i].field_val = val;
    } else {
      tmp.push({
        field_id: fieldID,
        field_val: val,
      });
    }
    onChange({ ...formData, custom_values: tmp });
  };
  const updateForm = (key: keyof IFormData, value: string) => {
    const val = value.length <= 0 ? null : Number(value);
    let tmp = { ...formData, [key]: val };
    if (
      (key === 'size_min' || key === 'size_max') &&
      tmp.size_min &&
      tmp.size_max
    ) {
      tmp.size_avg = getAverageSize(tmp.size_min, tmp.size_max);
    }
    onChange(tmp);
  };

  return (
    <div>
      {isMyFarm && (
        <div className='pb-17'>
          <OysterLineProbTable
            ref={tableRef}
            lineID={lineData.id}
            amountType={measureUnit}
            totalAmount={
              measureUnit === 'pcs'
                ? formData.estimated_amount ?? 0
                : formData.estimated_dozens ?? 0
            }
            prefillDisabled={true}
          />
        </div>
      )}
      {customAssesFields.map(field => {
        const cv = formData.custom_values?.find(x => x.field_id === field.id);

        return (
          <div key={field.id} className='mb-17'>
            {field.input_type === 'select' ? (
              <>
                <Dropdown
                  label={field.name}
                  options={
                    field.options
                      ?.split(',')
                      .map(t => ({ id: t, value: t, label: t })) ?? []
                  }
                  value={cv?.field_val ?? undefined}
                  onChange={v => updateCustomAssessValue(field.id, v)}
                  disabled={disabled}
                />
                {(!field.options || field.options.length <= 0) && (
                  <Link to={'/custom-fields'}>
                    <span style={{ color: '#008ed1' }}>Set up field</span>
                  </Link>
                )}
              </>
            ) : (
              <Input
                type={field.data_type === 'number' ? 'number' : 'text'}
                label={field.name}
                value={cv?.field_val ?? ''}
                onChange={e =>
                  updateCustomAssessValue(field.id, e.target.value)
                }
                unit={field.unit}
                disabled={disabled}
              />
            )}
          </div>
        );
      })}
      <CheckboxButton
        className='mb-17'
        label={translate(lang, 'Ready to harvest')}
        checked={formData.ready_to_harvest}
        onChange={e =>
          onChange({ ...formData, ready_to_harvest: e.target.checked })
        }
        disabled={disabled}
      />
      <div className='assessment-modal__condition d-flex justify-between'>
        <div className={'mxw-180'}>
          <Input
            type='number'
            label={`${translate(lang, 'Size min')} (mm)`}
            value={numToStr(formData.size_min)}
            onChange={e => updateForm('size_min', e.target.value)}
            required={true}
          />
        </div>
        <div className={'mxw-180 ml-12 mr-12'}>
          <Input
            type='number'
            label={`${translate(lang, 'Size max')} (mm)`}
            value={numToStr(formData.size_max)}
            onChange={e => updateForm('size_max', e.target.value)}
            required={true}
          />
        </div>
        <div className={'mxw-180'}>
          <Input
            type='number'
            label={`${translate(lang, 'Size avg')} (mm)`}
            value={numToStr(formData.size_avg)}
            onChange={e => updateForm('size_avg', e.target.value)}
            required={true}
          />
        </div>
      </div>
    </div>
  );
};

export default InstantOysterAssessment;
